import styles from './OpinionSlideContent.module.scss';
import sharedStyles from '@/modules/opinions/Shared.module.scss';

import { useLoginModal } from '@/LoginModalContext';
import { useComplete2048Task, useGetLanguage } from '@/hooks';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { useNavigation } from '@/hooks/useNavigation';
import {
  OpinionPoll,
  OpinionPollState,
  OpinionPollVoted,
  type OpinionResultParams,
  OpinionsApi,
  useOpinionContext,
} from '@/modules/opinions';
import { Answers } from '@/modules/opinions/components/OpinionSlideContent/components/Answers';
import { PAGE } from '@/modules/page/routes';
import { useStore } from '@/store';
import cn from 'classnames';
import { motion } from 'framer-motion';
import { t } from 'i18next';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import type { OpinionPollItem } from '../../types';
import { Actions, Header, Slider } from './components';

export const OpinionSlideContent = () => {
  const {
    isDragEnded,
    isVoted,
    answerItemId,
    setAnswerItemId,
    setSliderValue,
    setOpinion,
    opinion,
    sliderValue: predictPercentage,
  } = useOpinionContext();
  const [isShown, setIsShown] = useState(isVoted || !!opinion.votedFor);
  const { triggerHapticFeedback } = useHapticFeedback();
  const lang = useGetLanguage();
  const client = useQueryClient();
  const { currentPage, params } = useNavigation();

  const {
    startParams,
    pollsStore: { increaseOpinionsCount, selectedTopics },
    walletStore: { getJettonBalances },
    accountStore: { accountData },
    opinionsStore: { increaseOpinionsVotedCount },
    tournamentsStore: { updateOpinion, vote },
  } = useStore();
  const { id: profileId } = useParams();
  const me = profileId === accountData._id;
  const isAuthenticated = Boolean(accountData._id);
  const { openLoginModal } = useLoginModal();

  const ref = useRef<HTMLDivElement | null>(null);

  const complete2048TaskMutation = accountData.invited_by
    ? useComplete2048Task(accountData.invited_by)
    : null;

  const onVoteMutation = useMutation({
    mutationFn: async () => {
      if (currentPage === PAGE.TOURNAMENT_ID_OPINION_ID) {
        if (params?.id) {
          vote({
            opinionPollId: opinion._id,
            predictPercentage,
            itemId: answerItemId as string,
            tournamentId: params.id.toString(),
          });
          return Promise.resolve({
            ...opinion,
            state: OpinionPollState.VOTED,
            votedFor: answerItemId as string,
            actualPercentage: predictPercentage,
          });
        }
      }

      const payload = {
        id: opinion._id,
        predictPercentage,
        itemId: answerItemId as string,
      } as OpinionResultParams;

      if (startParams?.invite_code && startParams?.opId === opinion._id) {
        payload.askerCode = startParams.invite_code;
      }

      const updatedOpinion = await OpinionsApi.setOpinionResult(payload);

      let queryKey = [
        'opinions',
        'getOpinions',
        lang,
        selectedTopics.opinions,
      ] as any[];
      if (currentPage === PAGE.PROFILE_ID_OPINION_ID) {
        queryKey = ['profileOpinions', profileId, me, lang];
      }

      // Updating the cache of the list
      client.setQueryData<any>(
        queryKey,
        (oldData: { pages: OpinionPoll[][] }) => {
          if (!oldData) return oldData;

          const updatedPages = oldData.pages.map((page: OpinionPoll[]) =>
            page.map((opinion) =>
              opinion._id === updatedOpinion._id
                ? {
                    ...updatedOpinion,
                    title: opinion.title,
                    items: opinion.items,
                  }
                : opinion,
            ),
          );

          return {
            ...oldData,
            pages: updatedPages,
          };
        },
      );

      // Updating the specific opinion by ID, if you have a separate cache
      client.setQueryData<OpinionPoll>(
        [OpinionsApi.KEY, 'getOpinionById', updatedOpinion._id, lang],
        (old) => {
          if (!old) return updatedOpinion;

          return {
            ...updatedOpinion,
            title: old.title,
            items: old.items,
          };
        },
      );

      return updatedOpinion;
    },
    onSuccess: (data) => {
      if (currentPage !== PAGE.TOURNAMENT_ID_OPINION_ID) {
        // @ts-expect-error
        setOpinion((prev) => ({
          ...data,
          title: prev.title,
          items: prev.items,
        }));
        increaseOpinionsCount();
        client.refetchQueries({ queryKey: [OpinionsApi.KEY, data._id] });
        getJettonBalances(); // to show confetti
        if (complete2048TaskMutation) {
          complete2048TaskMutation.mutate({
            telegramId: accountData.id,
            taskId: 1,
          });
        }

        // adsonar
        if (
          isAuthenticated &&
          import.meta.env.VITE_ADSONAR_APP_ID &&
          import.meta.env.VITE_ADSONAR_AD_EVERY_N_OPINIONS
        ) {
          increaseOpinionsVotedCount();
        }
      } else {
        updateOpinion(data);
        setOpinion(data as OpinionPollVoted);
      }
    },
  });

  useEffect(() => {
    if (isVoted && !(opinion as OpinionPollVoted).actualPercentage) {
      const rightValue = opinion.votersChoices
        ? Object.keys(opinion.votersChoices)
            .filter((key) => key !== opinion.votedFor)
            .reduce((acc, key) => {
              acc =
                acc + (opinion.votersChoices ? opinion.votersChoices[key] : 0);
              return acc;
            }, 0)
        : 0;

      const leftValue = opinion.votersChoices![opinion.votedFor!];

      const actualPercentage =
        opinion.votedFor && opinion.votersChoices
          ? (opinion.votersChoices[opinion.votedFor] /
              Object.values(opinion.votersChoices).reduce((a, b) => a + b, 0)) *
            100
          : 0;

      setSliderValue(actualPercentage);

      setOpinion({
        ...opinion,
        leftValue,
        rightValue,
        actualPercentage,
      });
    }
  }, []);

  const onClick = (itemId: OpinionPollItem['itemId']) => {
    triggerHapticFeedback('medium');
    if (!isAuthenticated) {
      openLoginModal(() => {
        setAnswerItemId(itemId);
      });
    } else setAnswerItemId(itemId);
  };

  const [yes, no] = opinion.items;
  const isMultiple = opinion.items.length > 2;

  const opinionItem = opinion.items.find(
    ({ itemId }) => itemId === (isVoted ? opinion.votedFor : answerItemId),
  );

  const isNoSelected = no.itemId === answerItemId;

  return (
    <div className={styles.content}>
      <motion.div
        ref={ref}
        className={styles.blurImage}
        initial={{ y: '100%' }}
        animate={isVoted || answerItemId ? { y: 0 } : { y: '100%' }}
      >
        <span className={styles.wrapper}>
          <span
            className={cn(
              styles.blurWrapper,
              isNoSelected && styles.gradientNegative,
            )}
          />
          {opinionItem?.title && (
            <h2
              className={cn(
                styles.title,
                isNoSelected && styles.gradientNegative,
              )}
            >
              {opinionItem?.title}
            </h2>
          )}
        </span>
      </motion.div>
      <div className={styles.main}>
        <Header opinion={opinion as OpinionPollVoted} />
        {(isVoted || answerItemId) && (
          <p
            className={cn(styles.footerText, isVoted && sharedStyles.opacity0)}
          >
            {t('What percentage chose the same?')}
          </p>
        )}
        <div className={styles.body}>
          {isVoted || answerItemId ? (
            <>
              <div className={styles.sliderWrapper}>
                <Slider
                  isLoading={onVoteMutation.isPending}
                  onDragEnd={() => {
                    setIsShown(true);
                  }}
                />
                {isVoted && (
                  <div
                    className={cn(
                      styles.opinionsVotes,
                      (isDragEnded || isVoted) && styles.opinionsVotesVisible,
                    )}
                  >
                    <span>{opinion.leftValue}</span>
                    <span>{opinion.rightValue}</span>
                  </div>
                )}
              </div>
              <Actions
                isShown={isShown || isVoted}
                onClick={() => onVoteMutation.mutate()}
                disabled={onVoteMutation.isPending}
              />
            </>
          ) : (
            <Answers
              yes={yes}
              no={no}
              onClick={onClick}
              isDisabled={!!answerItemId}
              multipleAnswers={isMultiple}
              answers={opinion.items}
            />
          )}
        </div>
      </div>
    </div>
  );
};
